import { ApiDataTransformed, EmployeeRowData } from '../types';

// Helper function to remove category prefix from role name for display
const removeRoleCategory = (roleName: string): string => {
  // Remove category prefixes like "OTHER - ", "DSD - ", "NON-DSD - " etc.
  const dashMatch = roleName.match(/^(OTHER|DSD|NON-DSD|NONDSD)\s*-\s*(.+)$/i);
  if (dashMatch) return dashMatch[2];
  
  // Remove category suffixes in parentheses like " (OTHER)", " (DSD)", " (NON-DSD)" etc.
  const parenMatch = roleName.match(/^(.+?)\s*\((OTHER|DSD|NON-DSD|NONDSD)\)$/i);
  if (parenMatch) return parenMatch[1];
  
  return roleName;
};

/**
 * Validation function to check forecasted vs scheduled hours
 */
export const validateForecastedVsScheduledHours = (
  tableData: ApiDataTransformed[],
  employeeRows: EmployeeRowData[]
): string | null => {
  if (!tableData || tableData.length === 0) {
    return null; // No forecasted data to validate
  }

  if (!employeeRows || employeeRows.length === 0) {
    return null; // No employee schedule data to validate against
  }

  const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const zonesWithIssues: string[] = [];
  console.log('=== All Employee Rows Summary ===');
  employeeRows.forEach((row: EmployeeRowData, index: number) => {
    const totalHours = daysOfWeek.reduce((sum, day) => {
      return sum + (parseFloat(row[day as keyof EmployeeRowData] as string) || 0);
    }, 0);
    console.log(`Row ${index + 1}: Employee="${row.employee?.label || 'Unknown'}", Role="${row.role?.label || 'No Role'}", IsChild=${row.isChildRow}, ParentId=${row.parentId}, TotalHours=${totalHours}`);
  });
  console.log('=== End Employee Rows Summary ===');

  // Helper function to normalize role names for comparison
  const normalizeRoleName = (roleName: string): string => {
    return roleName
      .toLowerCase()
      .trim()
      .replace(/\s*\([^)]*\)$/, '') // Remove anything in parentheses at the end like "(DSD)" or "(NON-DSD)"
      .replace(/[-_\s]+/g, '') // Remove dashes, underscores, and spaces
  };

  // Check each zone/role in the forecasted data (tableData)
  tableData.forEach((forecastRow: ApiDataTransformed) => {
    const zoneName = forecastRow.roleName || 'Unknown Zone';

    // Skip the "Totals (All Roles)" row
    if (zoneName.toLowerCase().includes('totals')) {
      return;
    }

    // Track if this zone has any scheduled hours at all across all days
    let zoneHasAnyScheduledHours = false;

    // Get all employees (including split role children) with this role who have scheduled hours on any day
    const employeesWithRole = employeeRows.filter((employeeRow: EmployeeRowData) => {
      const employeeRole = employeeRow.role?.label || employeeRow.role?.value || '';
      const normalizedEmployeeRole = normalizeRoleName(employeeRole);
      const normalizedZoneName = normalizeRoleName(zoneName);

      const roleMatches = normalizedEmployeeRole === normalizedZoneName ||
             employeeRole.toLowerCase().trim() === zoneName.toLowerCase().trim() ||
             normalizedEmployeeRole.includes(normalizedZoneName) ||
             normalizedZoneName.includes(normalizedEmployeeRole) ||
             employeeRole.toLowerCase().includes(zoneName.toLowerCase()) ||
             zoneName.toLowerCase().includes(employeeRole.toLowerCase());
      
      return roleMatches;
    });

    // Check if any of these employees have scheduled hours on any day
    // This includes both regular employees and split role children
    employeesWithRole.forEach((employeeRow: EmployeeRowData) => {
      // Check all day hours for this employee
      daysOfWeek.forEach((day: string) => {
        const rawValue = employeeRow[day as keyof EmployeeRowData];
        const scheduledHours = parseFloat(rawValue as string) || 0;
        
        if (scheduledHours > 0) {
          zoneHasAnyScheduledHours = true;
        }
      });
    });

    // Also check parent rows that might have split child roles matching this zone
    // This handles cases where a parent employee is split into multiple roles
    if (!zoneHasAnyScheduledHours) {
      const parentRowsWithMatchingChildren = employeeRows.filter((parentRow: EmployeeRowData) => {
        if (parentRow.isChildRow) return false; // Skip child rows

        // Check if this parent has child rows with the matching role
        const childrenWithMatchingRole = employeeRows.filter((childRow: EmployeeRowData) => {
          if (!childRow.isChildRow || childRow.parentId !== parentRow.id) return false;
          
          const childRole = childRow.role?.label || childRow.role?.value || '';
          const normalizedChildRole = normalizeRoleName(childRole);
          const normalizedZoneName = normalizeRoleName(zoneName);

          return normalizedChildRole === normalizedZoneName ||
                 childRole.toLowerCase().trim() === zoneName.toLowerCase().trim() ||
                 normalizedChildRole.includes(normalizedZoneName) ||
                 normalizedZoneName.includes(normalizedChildRole) ||
                 childRole.toLowerCase().includes(zoneName.toLowerCase()) ||
                 zoneName.toLowerCase().includes(childRole.toLowerCase());
        });

        return childrenWithMatchingRole.length > 0;
      });

      // Check if any parent rows have hours (which would be distributed among children)
      parentRowsWithMatchingChildren.forEach((parentRow: EmployeeRowData) => {
        daysOfWeek.forEach((day: string) => {
          const scheduledHours = parseFloat(parentRow[day as keyof EmployeeRowData] as string) || 0;
          if (scheduledHours > 0) {
            zoneHasAnyScheduledHours = true;
          }
        });
      });
    }

    // Check if there are any forecasted hours for this zone on any day
    let zoneHasAnyForecastedHours = false;
    daysOfWeek.forEach((day: string) => {
      const dayData = forecastRow[day];
      if (dayData) {
        const forecastHours = parseFloat(dayData.fcst.toString()) || 0;
        if (forecastHours > 0) {
          zoneHasAnyForecastedHours = true;
        }
      }
    });

    // If zone has forecasted hours but no scheduled hours at all
    if (zoneHasAnyForecastedHours && !zoneHasAnyScheduledHours) {
      if (!zonesWithIssues.includes(zoneName)) {
        zonesWithIssues.push(zoneName);
      }
    }
  });

  if (zonesWithIssues.length > 0) {
    const zoneNames = zonesWithIssues.join(', ');
    return `There are forecasted hours for zone(s) ${zoneNames} but no hours scheduled. Please correct this before submitting.`;
  }

  return null; // No validation errors
};

/**
 * Validation function to check for hours violations (40+ hours, shift limits, daily limits)
 */
export const validateHoursViolations = (
  employeeRows: EmployeeRowData[],
  shiftTimes?: any
): string | null => {
  if (!employeeRows || employeeRows.length === 0) {
    return null; // No employee data to validate
  }

  let hasHoursViolations = false;

  // Helper function to get shift max hours
  const getShiftMaxHours = (shift: any): number => {
    if (!shift) return 12; // Default max hours per day

    const shiftData = shift.value ? JSON.parse(shift.value) : null;
    if (shiftData && shiftData.shift_type) {
      const shiftType = shiftData.shift_type.toLowerCase();
      if (shiftType.includes('5x8')) return 8;
      if (shiftType.includes('4x10')) return 10;
    }

    // Fallback to checking label if shift_type is not available
    const shiftLabel = shift.label?.toLowerCase() || '';
    if (shiftLabel.includes('5x8')) return 8;
    if (shiftLabel.includes('4x10')) return 10;

    return 12; // Default max hours per day
  };

  // Helper function to get day hours
  const getDayHours = (employeeRow: EmployeeRowData, day: string): number => {
    const dayValues: { [key: string]: string } = {
      sunday: employeeRow.sunday,
      monday: employeeRow.monday,
      tuesday: employeeRow.tuesday,
      wednesday: employeeRow.wednesday,
      thursday: employeeRow.thursday,
      friday: employeeRow.friday,
      saturday: employeeRow.saturday
    };
    return parseFloat(dayValues[day] || '0') || 0;
  };

  // Helper function to get weekly hours for an employee
  const getWeeklyHours = (employeeRow: EmployeeRowData): number => {
    const weekdayValues = [
      employeeRow.sunday,
      employeeRow.monday,
      employeeRow.tuesday,
      employeeRow.wednesday,
      employeeRow.thursday,
      employeeRow.friday,
      employeeRow.saturday
    ];

    let total = weekdayValues.reduce((sum, value) => {
      const numericValue = parseFloat(value) || 0;
      return sum + numericValue;
    }, 0);

    // If this is a parent row (not a child row and has split roles),
    // add the hours from all child split rows
    if (!employeeRow.isChildRow && employeeRow.isSplit) {
      const childRows = employeeRows.filter((row: EmployeeRowData) =>
        row.parentId === employeeRow.id
      );

      childRows.forEach((childRow: EmployeeRowData) => {
        const childWeekdayValues = [
          childRow.sunday,
          childRow.monday,
          childRow.tuesday,
          childRow.wednesday,
          childRow.thursday,
          childRow.friday,
          childRow.saturday
        ];

        const childTotal = childWeekdayValues.reduce((sum, value) => {
          const numericValue = parseFloat(value) || 0;
          return sum + numericValue;
        }, 0);

        total += childTotal;
      });
    }

    return total;
  };

  // Check all employee rows for violations
  employeeRows.forEach((employeeRow: EmployeeRowData) => {
    const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const maxHours = getShiftMaxHours(employeeRow.shift);
    const weeklyHours = getWeeklyHours(employeeRow);

    // Check for weekly hours over 40 (warning threshold)
    if (weeklyHours > 40) {
      hasHoursViolations = true;
    }

    // Check for daily hours violations
    daysOfWeek.forEach((day: string) => {
      const dayHours = getDayHours(employeeRow, day);

      // Check daily max exceeded (12 hours) or shift-specific max exceeded
      if (dayHours > 12 || dayHours > maxHours) {
        hasHoursViolations = true;
      }
    });
  });

  if (hasHoursViolations) {
    return 'One or more employees are scheduled for more than 40 hours. Submission is allowed, but please ensure this aligns with your scheduling policies';
  }

  return null; // No hours violations
};

/**
 * Check if any employee has more than 40 actual hours
 */
export const validateEmployeeHoursOver40 = (employeeRows: EmployeeRowData[]): boolean => {
  if (!employeeRows || employeeRows.length === 0) {
    return false;
  }

  // Helper function to get weekly hours for an employee
  const getWeeklyHours = (employeeRow: EmployeeRowData): number => {
    const weekdayValues = [
      employeeRow.sunday,
      employeeRow.monday,
      employeeRow.tuesday,
      employeeRow.wednesday,
      employeeRow.thursday,
      employeeRow.friday,
      employeeRow.saturday
    ];

    let total = weekdayValues.reduce((sum, value) => {
      const numericValue = parseFloat(value) || 0;
      return sum + numericValue;
    }, 0);

    // If this is a parent row (not a child row and has split roles),
    // add the hours from all child split rows
    if (!employeeRow.isChildRow && employeeRow.isSplit) {
      const childRows = employeeRows.filter((row: EmployeeRowData) =>
        row.parentId === employeeRow.id
      );

      childRows.forEach((childRow: EmployeeRowData) => {
        const childWeekdayValues = [
          childRow.sunday,
          childRow.monday,
          childRow.tuesday,
          childRow.wednesday,
          childRow.thursday,
          childRow.friday,
          childRow.saturday
        ];

        const childTotal = childWeekdayValues.reduce((sum, value) => {
          const numericValue = parseFloat(value) || 0;
          return sum + numericValue;
        }, 0);

        total += childTotal;
      });
    }

    return total;
  };

  // Check all employee rows for > 40 hours
  for (const employeeRow of employeeRows) {
    const weeklyHours = getWeeklyHours(employeeRow);
    if (weeklyHours > 40) {
      return true;
    }
  }

  return false;
};

/**
 * Check if any role has forecasted hours but no assigned employee
 */
export const validateUnassignedRoles = (
  tableData: ApiDataTransformed[],
  employeeRows: EmployeeRowData[]
): boolean => {
  if (!tableData || tableData.length === 0) {
    return false;
  }

  if (!employeeRows || employeeRows.length === 0) {
    return false;
  }

  // First, check for employees without roles assigned
  const hasEmployeesWithoutRoles = employeeRows.some((employeeRow: EmployeeRowData) => {
    const hasNoRole = !employeeRow.role?.label && !employeeRow.role?.value;
    const hasEmptyRole = (employeeRow.role?.label === '' || employeeRow.role?.label === 'Select Role') || 
                        (employeeRow.role?.value === '' || employeeRow.role?.value === 'Select Role');
    
    return employeeRow.employee?.label && (hasNoRole || hasEmptyRole);
  });

  if (hasEmployeesWithoutRoles) {
    return true;
  }

  const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  // Helper function to normalize role names for comparison
  const normalizeRoleName = (roleName: string): string => {
    return roleName
      .toLowerCase()
      .trim()
      .replace(/\s*\([^)]*\)$/, '') // Remove anything in parentheses at the end like "(DSD)" or "(NON-DSD)"
      .replace(/[-_\s]+/g, '') // Remove dashes, underscores, and spaces
  };

  // Check each zone/role in the forecasted data (tableData)
  for (const forecastRow of tableData) {
    const zoneName = forecastRow.roleName || 'Unknown Zone';

    // Skip the "Totals (All Roles)" row
    if (zoneName.toLowerCase().includes('totals')) {
      continue;
    }

    // Check if there are any forecasted hours for this zone on any day
    let zoneHasAnyForecastedHours = false;
    for (const day of daysOfWeek) {
      const dayData = forecastRow[day];
      if (dayData) {
        const forecastHours = parseFloat(dayData.fcst.toString()) || 0;
        if (forecastHours > 0) {
          zoneHasAnyForecastedHours = true;
          break;
        }
      }
    }

    if (!zoneHasAnyForecastedHours) {
      continue; // No forecasted hours for this zone
    }

    // Track if this zone has any scheduled hours at all across all days
    let zoneHasAnyScheduledHours = false;

    // Get all employees (including split role children) with this role who have scheduled hours on any day
    const employeesWithRole = employeeRows.filter((employeeRow: EmployeeRowData) => {
      const employeeRole = employeeRow.role?.label || employeeRow.role?.value || '';
      const normalizedEmployeeRole = normalizeRoleName(employeeRole);
      const normalizedZoneName = normalizeRoleName(zoneName);

      const roleMatches = normalizedEmployeeRole === normalizedZoneName ||
             employeeRole.toLowerCase().trim() === zoneName.toLowerCase().trim() ||
             normalizedEmployeeRole.includes(normalizedZoneName) ||
             normalizedZoneName.includes(normalizedEmployeeRole) ||
             employeeRole.toLowerCase().includes(zoneName.toLowerCase()) ||
             zoneName.toLowerCase().includes(employeeRole.toLowerCase());
      
      return roleMatches;
    });

    // Check if any of these employees have scheduled hours on any day
    for (const employeeRow of employeesWithRole) {
      for (const day of daysOfWeek) {
        const rawValue = employeeRow[day as keyof EmployeeRowData];
        const scheduledHours = parseFloat(rawValue as string) || 0;
        
        if (scheduledHours > 0) {
          zoneHasAnyScheduledHours = true;
          break;
        }
      }
      if (zoneHasAnyScheduledHours) break;
    }

    // Also check parent rows that might have split child roles matching this zone
    if (!zoneHasAnyScheduledHours) {
      const parentRowsWithMatchingChildren = employeeRows.filter((parentRow: EmployeeRowData) => {
        if (parentRow.isChildRow) return false; // Skip child rows

        // Check if this parent has child rows with the matching role
        const childrenWithMatchingRole = employeeRows.filter((childRow: EmployeeRowData) => {
          if (!childRow.isChildRow || childRow.parentId !== parentRow.id) return false;
          
          const childRole = childRow.role?.label || childRow.role?.value || '';
          const normalizedChildRole = normalizeRoleName(childRole);
          const normalizedZoneName = normalizeRoleName(zoneName);

          return normalizedChildRole === normalizedZoneName ||
                 childRole.toLowerCase().trim() === zoneName.toLowerCase().trim() ||
                 normalizedChildRole.includes(normalizedZoneName) ||
                 normalizedZoneName.includes(normalizedChildRole) ||
                 childRole.toLowerCase().includes(zoneName.toLowerCase()) ||
                 zoneName.toLowerCase().includes(childRole.toLowerCase());
        });

        return childrenWithMatchingRole.length > 0;
      });

      // Check if any parent rows have hours (which would be distributed among children)
      for (const parentRow of parentRowsWithMatchingChildren) {
        for (const day of daysOfWeek) {
          const scheduledHours = parseFloat(parentRow[day as keyof EmployeeRowData] as string) || 0;
          if (scheduledHours > 0) {
            zoneHasAnyScheduledHours = true;
            break;
          }
        }
        if (zoneHasAnyScheduledHours) break;
      }
    }

    // If zone has forecasted hours but no scheduled hours at all, return true (unassigned role found)
    if (!zoneHasAnyScheduledHours) {
      return true;
    }
  }

  return false; // No unassigned roles found
};

/**
 * Interface for detailed over-hours validation result
 */
export interface OverHoursValidationDetail {
  employeeName: string;
  totalHours: number;
  exceededDays: string[];
}

/**
 * Interface for detailed unassigned roles validation result
 */
export interface UnassignedRoleValidationDetail {
  employeeName?: string;
  roleName?: string;
  type: 'missing_role' | 'missing_employee';
}

/**
 * Interface for detailed forecasted vs scheduled hours validation result
 */
export interface ForecastVarianceDetail {
  roleName: string;
  forecastedHours: number;
  scheduledHours: number;
  variance: number;
  type: 'over' | 'under';
  exceededDays: string[];
}

/**
 * Get detailed information about employees who have more than 40 hours scheduled
 */
export const getEmployeesOver40Hours = (
  employeeRows: EmployeeRowData[]
): OverHoursValidationDetail[] => {
  if (!employeeRows || employeeRows.length === 0) {
    return [];
  }

  const details: OverHoursValidationDetail[] = [];
  const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Helper function to get weekly hours for an employee
  const getWeeklyHours = (employeeRow: EmployeeRowData): number => {
    const weekdayValues = [
      employeeRow.sunday,
      employeeRow.monday,
      employeeRow.tuesday,
      employeeRow.wednesday,
      employeeRow.thursday,
      employeeRow.friday,
      employeeRow.saturday
    ];

    let total = weekdayValues.reduce((sum, value) => {
      const numericValue = parseFloat(value) || 0;
      return sum + numericValue;
    }, 0);

    // If this is a parent row (not a child row and has split roles),
    // add the hours from all child split rows
    if (!employeeRow.isChildRow && employeeRow.isSplit) {
      const childRows = employeeRows.filter((row: EmployeeRowData) =>
        row.parentId === employeeRow.id
      );

      childRows.forEach((childRow: EmployeeRowData) => {
        const childWeekdayValues = [
          childRow.sunday,
          childRow.monday,
          childRow.tuesday,
          childRow.wednesday,
          childRow.thursday,
          childRow.friday,
          childRow.saturday
        ];

        const childTotal = childWeekdayValues.reduce((sum, value) => {
          const numericValue = parseFloat(value) || 0;
          return sum + numericValue;
        }, 0);

        total += childTotal;
      });
    }

    return total;
  };

  // Check each employee for over 40 hours
  employeeRows.forEach((employeeRow: EmployeeRowData) => {
    if (!employeeRow.employee?.label) return;
    
    const employeeName = employeeRow.employee.label;
    const totalHours = getWeeklyHours(employeeRow);
    
    // Only include employees with more than 40 hours
    if (totalHours > 40) {
      // Find which specific days have hours scheduled (for display purposes)
      const exceededDays: string[] = [];
      daysOfWeek.forEach((day, index) => {
        const dayHours = parseFloat(employeeRow[day as keyof EmployeeRowData] as string) || 0;
        if (dayHours > 0) {
          exceededDays.push(dayLabels[index]);
        }
      });
      
      details.push({
        employeeName,
        totalHours,
        exceededDays
      });
    }
  });

  console.log('Employees over 40 hours:', details);
  return details;
};

/**
 * Get detailed information about employees who exceed forecasted hours
 */
export const getEmployeesOverForecastedHours = (
  tableData: ApiDataTransformed[],
  employeeRows: EmployeeRowData[]
): OverHoursValidationDetail[] => {
  if (!tableData || tableData.length === 0 || !employeeRows || employeeRows.length === 0) {
    return [];
  }

  const details: OverHoursValidationDetail[] = [];
  const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Helper function to normalize role names for comparison
  const normalizeRoleName = (roleName: string): string => {
    return roleName
      .toLowerCase()
      .trim()
      .replace(/\s*\([^)]*\)$/, '') // Remove anything in parentheses at the end
      .replace(/[-_\s]+/g, '') // Remove dashes, underscores, and spaces
  };

  // Create a map of forecasted hours by role and day
  const forecastMap = new Map<string, { [key: string]: number }>();
  
  tableData.forEach((forecastRow: ApiDataTransformed) => {
    const roleName = forecastRow.roleName || '';
    if (roleName.toLowerCase().includes('totals')) return;
    
    const normalizedRole = normalizeRoleName(roleName);
    const dayHours: { [key: string]: number } = {};
    
    daysOfWeek.forEach(day => {
      const dayData = forecastRow[day];
      if (dayData) {
        dayHours[day] = parseFloat(dayData.fcst?.toString()) || 0;
      } else {
        dayHours[day] = 0;
      }
    });
    
    forecastMap.set(normalizedRole, dayHours);
  });

  // Check each employee for over-forecast hours
  employeeRows.forEach((employeeRow: EmployeeRowData) => {
    if (!employeeRow.employee?.label || !employeeRow.role?.label) return;
    
    const employeeName = employeeRow.employee.label;
    const normalizedRole = normalizeRoleName(employeeRow.role.label);
    const forecastedHours = forecastMap.get(normalizedRole);
    
    if (!forecastedHours) return;
    
    const exceededDays: string[] = [];
    let totalScheduledHours = 0;
    let totalForecastedHours = 0;
    
    daysOfWeek.forEach((day, index) => {
      const scheduledHours = parseFloat(employeeRow[day as keyof EmployeeRowData] as string) || 0;
      const forecastedDayHours = forecastedHours[day] || 0;
      
      totalScheduledHours += scheduledHours;
      totalForecastedHours += forecastedDayHours;
      
      if (scheduledHours > forecastedDayHours) {
        exceededDays.push(dayLabels[index]);
      }
    });
    
    // Add to details if total weekly hours exceed forecasted or any day exceeds
    if (totalScheduledHours > totalForecastedHours || exceededDays.length > 0) {
      details.push({
        employeeName,
        totalHours: totalScheduledHours,
        exceededDays
      });
    }
  });

  return details;
};

/**
 * Get detailed information about validation failures
 */
export const getUnassignedRoleDetails = (
  tableData: ApiDataTransformed[],
  employeeRows: EmployeeRowData[]
): UnassignedRoleValidationDetail[] => {
  if (!tableData || tableData.length === 0) {
    return [];
  }

  const details: UnassignedRoleValidationDetail[] = [];

  // Helper function to normalize role names for comparison
  const normalizeRoleName = (roleName: string): string => {
    return roleName
      .toLowerCase()
      .trim()
      .replace(/\s*\([^)]*\)$/, '') // Remove anything in parentheses at the end
      .replace(/[-_\s]+/g, '') // Remove dashes, underscores, and spaces
  };

  // Find employees without roles
  if (employeeRows && employeeRows.length > 0) {
    employeeRows.forEach((employeeRow: EmployeeRowData) => {
      // Check for missing role (no role assigned at all)
      const hasNoRole = !employeeRow.role?.label && !employeeRow.role?.value;
      // Also check for empty role (role exists but is empty)
      const hasEmptyRole = (employeeRow.role?.label === '' || employeeRow.role?.label === 'Select Role') || 
                          (employeeRow.role?.value === '' || employeeRow.role?.value === 'Select Role');
      
      if (employeeRow.employee?.label && (hasNoRole || hasEmptyRole)) {
        console.log(`Found employee without proper role: ${employeeRow.employee.label}`);
        details.push({
          employeeName: employeeRow.employee.label,
          type: 'missing_role'
        });
      }
    });
  }

  // Find roles with forecasted hours but no assigned employees
  const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  
  tableData.forEach((forecastRow: ApiDataTransformed) => {
    const roleName = removeRoleCategory(forecastRow.roleName || '');
    if (roleName.toLowerCase().includes('totals')) return;
    
    // Check if this role has any forecasted hours
    const hasForecastedHours = daysOfWeek.some(day => {
      const dayData = forecastRow[day];
      if (dayData) {
        const hours = parseFloat(dayData.fcst?.toString()) || 0;
        return hours > 0;
      }
      return false;
    });
    
    if (!hasForecastedHours) return;
    
    // Check if any employee is assigned to this role with scheduled hours
    const normalizedRole = normalizeRoleName(roleName);
    const hasAssignedEmployee = employeeRows && employeeRows.some((employeeRow: EmployeeRowData) => {
      if (!employeeRow.role?.label) return false;
      
      const employeeNormalizedRole = normalizeRoleName(employeeRow.role.label);
      if (employeeNormalizedRole !== normalizedRole) return false;
      
      // Check if employee has any scheduled hours
      return daysOfWeek.some(day => {
        const hours = parseFloat(employeeRow[day as keyof EmployeeRowData] as string) || 0;
        return hours > 0;
      });
    });
    
    if (!hasAssignedEmployee) {
      console.log(`Found role without assigned employee: ${roleName}`);
      details.push({
        roleName: roleName,
        type: 'missing_employee'
      });
    }
  });

  console.log('Final validation details:', details);
  return details;
};

/**
 * Check if any role has scheduled hours that differ from forecasted hours (over or under)
 */
export const validateForecastVariance = (
  tableData: ApiDataTransformed[],
  employeeRows: EmployeeRowData[]
): boolean => {
  if (!tableData || tableData.length === 0 || !employeeRows || employeeRows.length === 0) {
    return false;
  }

  const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  // Helper function to normalize role names for comparison
  const normalizeRoleName = (roleName: string): string => {
    return roleName
      .toLowerCase()
      .trim()
      .replace(/\s*\([^)]*\)$/, '') // Remove parenthetical text
      .replace(/[-_\s]+/g, ''); // Remove spaces, hyphens, underscores
  };

  // Check each role in the forecasted data
  for (const forecastRow of tableData) {
    const roleName = forecastRow.roleName || 'Unknown Role';
    
    // Skip the "Totals (All Roles)" row
    if (roleName.toLowerCase().includes('totals')) {
      continue;
    }

    // Calculate total scheduled hours for this role
    let totalScheduledHours = 0;
    const normalizedForecastRole = normalizeRoleName(roleName);

    employeeRows.forEach((employeeRow: EmployeeRowData) => {
      if (!employeeRow.role?.label) return;
      
      const normalizedEmployeeRole = normalizeRoleName(employeeRow.role.label);
      
      if (normalizedEmployeeRole === normalizedForecastRole) {
        daysOfWeek.forEach(day => {
          const hours = parseFloat(employeeRow[day as keyof EmployeeRowData] as string) || 0;
          totalScheduledHours += hours;
        });
      }
    });

    // Get total forecasted hours for this role
    const totalForecastedHours = daysOfWeek.reduce((sum, day) => {
      const dayData = forecastRow[day];
      if (dayData && typeof dayData === 'object' && 'fcst' in dayData) {
        return sum + (dayData.fcst || 0);
      }
      return sum;
    }, 0);

    // Check if there's a variance (allowing for small rounding differences)
    const variance = Math.abs(totalScheduledHours - totalForecastedHours);
    if (variance > 0.1) { // Allow for small rounding differences
      console.log(`Found forecast variance for role ${roleName}: Forecasted=${totalForecastedHours}, Scheduled=${totalScheduledHours}, Variance=${variance}`);
      return true;
    }
  }

  return false;
};

/**
 * Get detailed information about roles with forecast variance
 */
export const getForecastVarianceDetails = (
  tableData: ApiDataTransformed[],
  employeeRows: EmployeeRowData[]
): ForecastVarianceDetail[] => {
  if (!tableData || tableData.length === 0 || !employeeRows || employeeRows.length === 0) {
    return [];
  }

  const details: ForecastVarianceDetail[] = [];
  const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Helper function to normalize role names for comparison
  const normalizeRoleName = (roleName: string): string => {
    return roleName
      .toLowerCase()
      .trim()
      .replace(/\s*\([^)]*\)$/, '') // Remove parenthetical text
      .replace(/[-_\s]+/g, ''); // Remove spaces, hyphens, underscores
  };

  // Check each role in the forecasted data
  tableData.forEach((forecastRow: ApiDataTransformed) => {
    const roleName = removeRoleCategory(forecastRow.roleName || 'Unknown Role');
    
    // Skip the "Totals (All Roles)" row
    if (roleName.toLowerCase().includes('totals')) {
      return;
    }

    // Calculate total scheduled hours for this role and track which days have variances
    let totalScheduledHours = 0;
    const exceededDays: string[] = [];
    const normalizedForecastRole = normalizeRoleName(roleName);

    // Calculate scheduled hours by day and identify days with variances
    daysOfWeek.forEach((day, dayIndex) => {
      let dayScheduledHours = 0;
      
      employeeRows.forEach((employeeRow: EmployeeRowData) => {
        if (!employeeRow.role?.label) return;
        
        const normalizedEmployeeRole = normalizeRoleName(employeeRow.role.label);
        
        if (normalizedEmployeeRole === normalizedForecastRole) {
          const hours = parseFloat(employeeRow[day as keyof EmployeeRowData] as string) || 0;
          dayScheduledHours += hours;
        }
      });

      totalScheduledHours += dayScheduledHours;

      // Check if this day has a variance
      const dayData = forecastRow[day];
      const dayForecastedHours = (dayData && typeof dayData === 'object' && 'fcst' in dayData) ? (dayData.fcst || 0) : 0;
      
      if (Math.abs(dayScheduledHours - dayForecastedHours) > 0.1) {
        exceededDays.push(dayLabels[dayIndex]);
      }
    });

    // Get total forecasted hours for this role
    const totalForecastedHours = daysOfWeek.reduce((sum, day) => {
      const dayData = forecastRow[day];
      if (dayData && typeof dayData === 'object' && 'fcst' in dayData) {
        return sum + (dayData.fcst || 0);
      }
      return sum;
    }, 0);

    // Check if there's a variance (allowing for small rounding differences)
    const variance = totalScheduledHours - totalForecastedHours;
    if (Math.abs(variance) > 0.1) {
      details.push({
        roleName,
        forecastedHours: Number(totalForecastedHours.toFixed(1)),
        scheduledHours: Number(totalScheduledHours.toFixed(1)),
        variance: Number(Math.abs(variance).toFixed(1)),
        type: variance > 0 ? 'over' : 'under',
        exceededDays
      });
    }
  });

  console.log('Forecast variance details:', details);
  return details;
};